import { FormattedMessage } from 'react-intl';

import { Props as FormattedMessageProps } from 'react-intl/src/components/message';

import Info from '@mui/icons-material/Info';
import { Alert, AlertTitle, Grid, Link, Stack, Tooltip, Typography } from '@mui/material';

import { CONFIG } from '@arrived/config';
import { AssetType, Offering } from '@arrived/models';

import { PROPERTY_HASH_MAP } from '../../Property.constants';
import { PropertyDetailsSection } from '../PropertyDetailsSection';
import { LeverageContent } from './LeverageContent';
import { VolatilityContent } from './VolatilityContent';

export interface LeverageAndVolatilityProps {
  debtPercent: number;
  offering: Offering;
}

interface VolatilityTitleProps extends Omit<FormattedMessageProps, 'id'> {
  assetType: AssetType;
}

const VolatilityTitle = ({ assetType, ...rest }: VolatilityTitleProps) => {
  switch (assetType) {
    case AssetType.LTR:
      return <FormattedMessage id="common.relative.volatility" {...rest} />;
    case AssetType.STR:
      return <FormattedMessage id="common.property.equity.volatility" {...rest} />;
  }
};

export const LeverageAndVolatility = ({ debtPercent, offering }: LeverageAndVolatilityProps) => {
  return (
    <PropertyDetailsSection
      description={
        <FormattedMessage
          id="property-leverage-and-volatility-description"
          defaultMessage="The potential financial returns you can earn are often linked to the potential risk and volatility. Adding leverage or debt to properties can amplify the potential return in exchange for higher potential volatility."
        />
      }
      hash={PROPERTY_HASH_MAP.volatility}
      title={<FormattedMessage id="property-leverage-and-volatility" defaultMessage="Property Leverage & Volatility" />}
    >
      <Grid container columnSpacing={2} rowSpacing={4}>
        {offering.hasFlexFinancing && (
          <Grid item xs={12}>
            <Alert severity="info">
              <AlertTitle sx={{ fontWeight: 'semibold' }}>
                <FormattedMessage id="common.flexible.future.financing" />
              </AlertTitle>
              <Typography variant="body1">
                <FormattedMessage
                  id="flexible.future.financing.description"
                  values={{
                    a: (parts: any) => (
                      <Link
                        href={`${CONFIG.helpUrl}articles/6843245-will-arrived-refinance-properties-in-the-future`}
                        fontWeight="semibold"
                      >
                        {parts}
                      </Link>
                    ),
                  }}
                />
              </Typography>
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} lg={6}>
          <Tooltip
            title={
              <FormattedMessage
                id="property-leverage-explanation"
                defaultMessage="Leverage uses borrowed capital or debt to increase the potential return of an investment. Leverage works to your advantage when real estate values rise, but it can also lead to losses if values decline."
              />
            }
          >
            <Typography
              variant="h5.semibold"
              sx={{ cursor: 'pointer' }}
              component={Stack}
              direction="row"
              alignItems="center"
              gap={1}
            >
              <FormattedMessage id="property-leverage" defaultMessage="Property Leverage" />
              <Info sx={{ color: 'font.400' }} />
            </Typography>
          </Tooltip>
          <LeverageContent debtPercent={debtPercent} sx={{ pt: 1 }} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Tooltip
            title={
              <FormattedMessage
                id="relative-volatility-explanation"
                defaultMessage="Investment volatility refers to the range of values that a particular asset might move between. As an example, higher volatility might mean higher potential movements up or down in the value of the investment over time."
              />
            }
          >
            <Typography
              variant="h5.semibold"
              sx={{ cursor: 'pointer' }}
              component={Stack}
              direction="row"
              alignItems="center"
              gap={1}
            >
              <VolatilityTitle assetType={offering.assetType} />
              <Info sx={{ color: 'font.400' }} />
            </Typography>
          </Tooltip>
          <VolatilityContent debtPercent={debtPercent} sx={{ pt: 1 }} />
        </Grid>
      </Grid>
    </PropertyDetailsSection>
  );
};
